import React, { useEffect, useState, useCallback } from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import axios from "axios";
import swal from "sweetalert";
import { imageurl } from "../../../App";
import { isDefaultStoreImage } from "../../../util/storeLogoHelper";
import {
  FaArrowLeft,
  FaSave,
  FaBoxes,
  FaTags,
  FaTrashAlt,
  FaExternalLinkAlt,
  FaEye,
  FaStar,
  FaFire,
  FaPlus,
  FaMinus,
} from "react-icons/fa";
import ShopifySEOEditor from "../common/ShopifySEOEditor";
import ProductVariantsEditor, { catalogFromDraft, draftFromCatalog } from "./ProductVariantsEditor";
import { checkImageFile, focusFirstError, hasApiErrors, readApiErrors } from "../../../util/formErrors";

export default function EditProduct(props) {
  const history = useHistory();
  const routeParams = useParams();
  const productId = props.match?.params?.id || routeParams?.id;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [errors, setErrors] = useState({});
  const [variants, setVariants] = useState({ options: [], items: [], error: "" });

  // Form State
  const [formData, setFormData] = useState({
    category_id: "",
    name: "",
    slug: "",
    brand: "",
    selling_price: "",
    cost_price: "",
    qty: 0,
    discription: "",
    meta_title: "",
    meta_keyword: "",
    meta_descrip: "",
    featured: false,
    popular: false,
    on_store: true,
  });

  // Images state
  const [existingImages, setExistingImages] = useState([]);
  const [newImages, setNewImages] = useState([]);
  const [newImagePreviews, setNewImagePreviews] = useState([]);

  const fetchInitialData = useCallback(() => {
    setLoading(true);

    const fetchCategories = axios.get(`/api/all-category`);
    const fetchProduct = axios.get(`/api/edit-product/${productId}`);

    Promise.all([fetchCategories, fetchProduct])
      .then(([catRes, prodRes]) => {
        if (catRes.data.status === 200) {
          setCategories(catRes.data.category || []);
        }

        if (prodRes.data.status === 200) {
          const prod = prodRes.data.product;
          const priceObj = prod.productprices_admin;

          const sellingPrice = priceObj
            ? priceObj.seeling_price
            : prod.selling_price || 0;
          const costPrice =
            prod.cost_price ??
            priceObj?.cost_price ??
            prodRes.data.cost_price ??
            prod.original_price ??
            prod.orignal_price ??
            priceObj?.original_price ??
            "";
          const qty = priceObj
            ? priceObj.qty
            : prod.qty !== undefined && prod.qty !== null
            ? prod.qty
            : 0;

          setFormData({
            category_id: prod.category_id || "",
            name: prod.name || "",
            slug: prod.slug || "",
            brand: prod.brand || "",
            selling_price: sellingPrice,
            cost_price: costPrice,
            qty: Number(qty),
            discription: prod.discription || prod.description || "",
            meta_title: prod.meta_title || prod.name || "",
            meta_keyword: prod.meta_keyword || "",
            meta_descrip: prod.meta_descrip || "",
            featured: Boolean(Number(prod.featured) === 1 || prod.featured === true || prod.featured === "1"),
            popular: Boolean(Number(prod.popular) === 1 || prod.popular === true || prod.popular === "1"),
            on_store: Boolean(Number(prod.status) === 1 || prod.status === true || prod.status === "1")
              && (prod.store_visibility === undefined || prod.store_visibility === null || Number(prod.store_visibility) === 1 || prod.store_visibility === true || prod.store_visibility === "1"),
          });

          // Existing images (strictly filter out default store logo / placeholder)
          const allCandidateImgs = [];
          if (Array.isArray(prod.product_image) && prod.product_image.length > 0) {
            prod.product_image.forEach((item) => allCandidateImgs.push(item));
          } else if (Array.isArray(prod.productImage) && prod.productImage.length > 0) {
            prod.productImage.forEach((item) => allCandidateImgs.push(item));
          }

          if (allCandidateImgs.length === 0 && prod.image && !isDefaultStoreImage(prod.image)) {
            allCandidateImgs.push({ id: "primary", image: prod.image });
          }

          const realImgs = allCandidateImgs.filter((item) => {
            const p = typeof item === "string" ? item : item?.image;
            return p && !isDefaultStoreImage(p);
          });

          setExistingImages(realImgs);
          setVariants(draftFromCatalog(prod.variant_catalog));
        } else if (prodRes.data.status === 404) {
          swal("Not Found", prodRes.data.message || "Product not found", "error");
          history.push("/admin/products");
        } else if (prodRes.data.status === 205) {
          swal("Access Denied", prodRes.data.message || "Admin authorization required", "error");
          history.push("/admin/products");
        }
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        swal("Error", "Failed to load product details", "error");
      });
  }, [productId, history]);

  useEffect(() => {
    document.title = "Edit Product — Admin Catalog";
    fetchInitialData();
  }, [fetchInitialData]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  // Auto-generate slug from name if needed
  const handleAutoSlug = () => {
    if (!formData.name) return;
    const cleanSlug = formData.name
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, "")
      .replace(/[\s_-]+/g, "-")
      .replace(/^-+|-+$/g, "");
    setFormData((prev) => ({ ...prev, slug: cleanSlug }));
  };

  // Image File Selection with live previews
  // Each photo is checked when chosen (same rules as the server); good ones are kept.
  const handleFileChange = (e) => {
    const chosen = Array.from(e.target.files || []);
    const problems = [];
    const files = chosen.filter((file) => {
      const problem = checkImageFile(file);
      if (problem) problems.push(problem);
      return !problem;
    });
    if (problems.length) e.target.value = "";
    setNewImages(files);
    setNewImagePreviews(files.map((file) => URL.createObjectURL(file)));
    setErrors((prev) => ({ ...prev, image: problems.join(" ") || null }));
  };

  // Delete existing server image
  const handleDeleteExistingImage = (imageId) => {
    swal({
      title: "Delete this image?",
      text: "This image will be permanently removed from the product gallery.",
      icon: "warning",
      buttons: ["Cancel", "Yes, Delete"],
      dangerMode: true,
    }).then((willDelete) => {
      if (willDelete) {
        const deleteUrl = imageId === "primary"
          ? `/api/delete-product-image/primary?product_id=${productId}`
          : `/api/delete-product-image/${imageId}?product_id=${productId}`;

        axios
          .delete(deleteUrl)
          .then((res) => {
            if (res.data.status === 200) {
              setExistingImages((prev) => prev.filter((img) => img.id !== imageId));
              swal("Deleted", "Image removed successfully", "success", {
                buttons: false,
                timer: 1000,
              });
            } else {
              setExistingImages((prev) => prev.filter((img) => img.id !== imageId));
            }
          })
          .catch(() => {
            setExistingImages((prev) => prev.filter((img) => img.id !== imageId));
          });
      }
    });
  };

  // Form Submit Handler
  const handleSubmit = (e) => {
    e.preventDefault();

    // Basic frontend validations
    const validationErrors = {};
    const unitCost = Number(formData.cost_price || 0);
    const built = catalogFromDraft(variants);
    const usingVariants = !built.error && built.options.length > 0;
    const sellingPrice = usingVariants ? built.selling : Number(formData.selling_price || 0);

    if (!formData.name.trim()) validationErrors.name = "Product name is required.";
    if (!formData.category_id) validationErrors.category_id = "Please select a category.";
    if (built.error && (variants.options || []).some((option) => option.name || option.valuesText)) {
      validationErrors.variants = built.error;
    }
    if (!usingVariants && (formData.selling_price === "" || sellingPrice <= 0)) {
      validationErrors.selling_price = "Selling price must be greater than 0.";
    } else if (!usingVariants && unitCost > 0 && sellingPrice <= unitCost) {
      validationErrors.selling_price = `Selling Price (Rs. ${sellingPrice}) must be greater than Unit Cost / Buying Price (Rs. ${unitCost}).`;
      validationErrors.cost_price = `Unit Cost / Buying Price must be less than Selling Price (Rs. ${sellingPrice}).`;
    }
    if (!usingVariants && (formData.qty === "" || Number(formData.qty) < 0)) {
      validationErrors.qty = "Stock quantity cannot be negative.";
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      focusFirstError();
      return;
    }

    setSaving(true);

    const payload = new FormData();
    payload.append("category_id", formData.category_id);
    payload.append("name", formData.name.trim());
    payload.append("slug", formData.slug.trim() || formData.name.toLowerCase().replace(/\s+/g, "-"));
    payload.append("brand", formData.brand.trim());
    const choiceCosts = (built.items || []).map((item) => Number(item.cost_price || 0)).filter((cost) => cost > 0);
    const rolledCost = choiceCosts.length ? Math.min(...choiceCosts) : 0;
    payload.append("selling_price", usingVariants ? built.selling : formData.selling_price);
    payload.append("cost_price", usingVariants ? rolledCost : (formData.cost_price || 0));
    payload.append("qty", usingVariants ? built.qty : formData.qty);
    payload.append("variants", JSON.stringify({
      options: usingVariants ? built.options : [],
      items: usingVariants ? built.items : [],
    }));
    payload.append("discription", formData.discription);
    payload.append("meta_title", formData.meta_title.trim() || formData.name.trim());
    payload.append("meta_keyword", formData.meta_keyword.trim());
    payload.append("meta_descrip", formData.meta_descrip.trim());
    payload.append("featured", formData.featured ? 1 : 0);
    payload.append("popular", formData.popular ? 1 : 0);
    payload.append("status", formData.on_store ? 1 : 0);
    payload.append("store_visibility", formData.on_store ? 1 : 0);

    const userId = sessionStorage.getItem("id");
    if (userId) {
      payload.append("updated_by", userId);
    }

    // Append newly selected images
    for (let i = 0; i < newImages.length; i++) {
      payload.append("image[]", newImages[i]);
    }

    // Errors arrive as a 200 reply with status 4xx, or as an HTTP error: show both under the fields.
    const showErrors = (source) => {
      const { message, fields } = readApiErrors(source);
      if (fields.original_price && !fields.cost_price) fields.cost_price = fields.original_price;
      setErrors(fields);
      focusFirstError();
      swal("Product not saved", message || "Please check the highlighted fields.", "error");
    };
    axios
      .post(`/api/update-product/${productId}`, payload)
      .then((res) => {
        setSaving(false);
        if (res.data.status === 200) {
          if (res.data.slug) {
            setFormData((prev) => ({ ...prev, slug: res.data.slug }));
          }
          swal({
            title: "Product Updated!",
            text: res.data.message || `Changes to "${formData.name}" have been saved successfully.`,
            icon: "success",
            buttons: {
              stay: "Keep Editing",
              view: "View Products",
            },
          }).then((value) => {
            if (value === "view") {
              history.push("/admin/products");
            } else {
              fetchInitialData();
              setNewImages([]);
              setNewImagePreviews([]);
            }
          });
        } else if (hasApiErrors(res)) {
          showErrors(res);
        } else {
          swal("Error", res.data.message || "Failed to update product.", "error");
        }
      })
      .catch((err) => {
        setSaving(false);
        showErrors(err);
      });
  };

  if (loading) {
    return (
      <div className="container-fluid py-5 text-center">
        <div className="spinner-border text-primary" role="status"></div>
        <p className="text-muted small mt-2">Loading product data...</p>
      </div>
    );
  }

  const selectedCategoryObj = categories.find((c) => String(c.id) === String(formData.category_id));
  const currentStock = Number(formData.qty || 0);
  const sellingPriceNum = Number(formData.selling_price || 0);
  const unitCostNum = Number(formData.cost_price || 0);
  const grossProfitPerUnit = sellingPriceNum - unitCostNum;
  const marginPercent = sellingPriceNum > 0 ? ((grossProfitPerUnit / sellingPriceNum) * 100).toFixed(1) : 0;
  const totalStockCost = currentStock * unitCostNum;
  const totalValuation = currentStock * sellingPriceNum;
  const hasChoices = (variants.options || []).length > 0;
  const variantPreview = catalogFromDraft(variants);
  const choicesReady = hasChoices && !variantPreview.error && (variantPreview.items || []).length > 0;
  const shownStock = hasChoices ? Number(variantPreview.qty || 0) : currentStock;

  return (
    <div className="container-fluid px-0">
      {/* Top Header & Breadcrumbs */}
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
        <div>
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb mb-1 small">
              <li className="breadcrumb-item">
                <Link to="/admin/products" className="text-decoration-none text-muted">
                  Catalog Products
                </Link>
              </li>
              <li className="breadcrumb-item active text-dark fw-semibold" aria-current="page">
                Edit #{productId}
              </li>
            </ol>
          </nav>
          <div className="d-flex align-items-center gap-2">
            <h3 className="fw-bold mb-0 text-dark" style={{ letterSpacing: "-0.5px" }}>
              {formData.name || "Edit Product"}
            </h3>
            <span
              className={`badge rounded-pill px-2.5 py-1 ${
                currentStock > 5
                  ? "bg-success bg-opacity-10 text-success"
                  : currentStock > 0
                  ? "bg-warning bg-opacity-15 text-warning"
                  : "bg-danger bg-opacity-10 text-danger"
              }`}
            >
              {currentStock > 5 ? "In Stock" : currentStock > 0 ? "Low Stock" : "Out of Stock"}
            </span>
          </div>
        </div>

        <div className="d-flex align-items-center gap-2">
          <Link
            to="/admin/products"
            className="btn btn-outline-secondary d-inline-flex align-items-center gap-2 rounded-3 px-3 py-2 fw-semibold"
          >
            <FaArrowLeft /> Back to Products
          </Link>
          {formData.slug && (
            <Link
              to={`/collection/${selectedCategoryObj?.slug || "all"}/${formData.slug}`}
              target="_blank"
              className="btn btn-outline-dark d-inline-flex align-items-center gap-2 rounded-3 px-3 py-2 fw-semibold"
            >
              <FaExternalLinkAlt size={12} /> View in Store
            </Link>
          )}
          <button
            type="button"
            onClick={handleSubmit}
            disabled={saving}
            className="btn btn-primary d-inline-flex align-items-center gap-2 rounded-3 px-4 py-2 fw-semibold shadow-sm"
          >
            {saving ? (
              <>
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                Saving...
              </>
            ) : (
              <>
                <FaSave /> Save Changes
              </>
            )}
          </button>
        </div>
      </div>

      {/* Main Form */}
      <form onSubmit={handleSubmit}>
        <div className="row g-4">
          {/* LEFT COLUMN: Main Product Attributes */}
          <div className="col-12 col-lg-8">
            {/* Card 1: Core Information */}
            <div className="admin-card bg-white rounded-3 shadow-sm border-0 mb-4 p-4">
              <h5 className="fw-bold text-dark mb-3 d-flex align-items-center gap-2 border-bottom pb-2">
                <FaBoxes className="text-primary" /> Product Identity & Categorization
              </h5>

              <div className="row g-3">
                <div className="col-12">
                  <label className="form-label fw-semibold text-dark small mb-1">
                    Product Title / Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    className={`form-control ${errors.name ? "is-invalid" : ""}`}
                    placeholder="e.g. Cerave Hydrating Cleanser 236ml"
                    value={formData.name}
                    onChange={handleInputChange}
                    onBlur={handleAutoSlug}
                  />
                  {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                </div>

                <div className="col-12 col-md-6">
                  <label className="form-label fw-semibold text-dark small mb-1">
                    Primary Category <span className="text-danger">*</span>
                  </label>
                  <select
                    name="category_id"
                    className={`form-select ${errors.category_id ? "is-invalid" : ""}`}
                    value={formData.category_id}
                    onChange={handleInputChange}
                  >
                    <option value="">Select a Category</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                  {errors.category_id && <div className="invalid-feedback">{errors.category_id}</div>}
                </div>

                <div className="col-12 col-md-6">
                  <label className="form-label fw-semibold text-dark small mb-1">
                    Brand / Manufacturer
                  </label>
                  <input
                    type="text"
                    name="brand"
                    className="form-control"
                    placeholder="e.g. CeraVe, The Ordinary, Jenpharm"
                    value={formData.brand}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="col-12">
                  <label className="form-label fw-semibold text-dark small mb-1">
                    URL Slug / Handle
                  </label>
                  <div className="input-group input-group-sm">
                    <span className="input-group-text bg-light text-muted">/collection/.../</span>
                    <input
                      type="text"
                      name="slug"
                      className={`form-control font-monospace ${errors.slug ? "is-invalid" : ""}`}
                      value={formData.slug}
                      onChange={handleInputChange}
                      placeholder="auto-generated-slug"
                    />
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={handleAutoSlug}
                    >
                      Regenerate
                    </button>
                  </div>
                  {errors.slug && <small className="text-danger d-block mt-1">{errors.slug}</small>}
                </div>

                <div className="col-12">
                  <label className="form-label fw-semibold text-dark small mb-1">
                    Product Description
                  </label>
                  <textarea
                    name="discription"
                    rows="4"
                    className="form-control"
                    placeholder="Detailed description of features, benefits, usage instructions..."
                    value={formData.discription}
                    onChange={handleInputChange}
                  ></textarea>
                </div>
              </div>
            </div>

            <div className="admin-card bg-white rounded-3 shadow-sm border-0 mb-4 p-4">
              <h5 className="fw-bold text-dark mb-3 border-bottom pb-2">Sizes, colors, and other choices</h5>
              {errors.variants && <div className="field-error alert alert-danger py-2 px-3 mb-2 small" role="alert">{errors.variants}</div>}
              <ProductVariantsEditor value={variants} onChange={setVariants} />
            </div>

            {/* Card 2: Pricing & Inventory Control */}
            <div className="admin-card bg-white rounded-3 shadow-sm border-0 mb-4 p-4">
              <h5 className="fw-bold text-dark mb-3 d-flex align-items-center gap-2 border-bottom pb-2">
                <FaTags className="text-success" /> Pricing & Inventory Tracking
              </h5>

              <div className="row g-3">
                {hasChoices ? (
                  <div className="col-12">
                    <div className="alert alert-light border mb-0">
                      <strong>Selling price, buying price, and stock are on each choice above.</strong>
                      <span className="d-block mt-1">
                        {choicesReady
                          ? `Customers see prices from Rs. ${Number(variantPreview.selling || 0).toLocaleString()}. Total stock is ${shownStock}.`
                          : "Fill in the selling price, buying price, and stock on every row."}
                      </span>
                    </div>
                  </div>
                ) : (
                <div className="col-12 col-md-4">
                  <label className="form-label fw-semibold text-dark small mb-1">
                    Selling Price (PKR) <span className="text-danger">*</span>
                  </label>
                  <div className="input-group">
                    <span className="input-group-text fw-semibold text-muted">Rs.</span>
                    <input
                      type="number"
                      step="any"
                      min="0"
                      name="selling_price"
                      className={`form-control fw-bold fs-6 font-monospace ${
                        errors.selling_price ? "is-invalid" : ""
                      }`}
                      placeholder="0.00"
                      value={formData.selling_price}
                      onChange={handleInputChange}
                    />
                    {errors.selling_price && (
                      <div className="invalid-feedback">{errors.selling_price}</div>
                    )}
                  </div>
                  <small className="text-muted d-block mt-1">Retail price for shoppers</small>
                </div>
                )}

                {!hasChoices && (
                <div className="col-12 col-md-4">
                  <label className="form-label fw-semibold text-dark small mb-1">
                    Unit Cost / Buying Price (PKR)
                  </label>
                  <div className="input-group">
                    <span className="input-group-text fw-semibold text-muted">Rs.</span>
                    <input
                      type="number"
                      step="any"
                      min="0"
                      name="cost_price"
                      className={`form-control fw-bold fs-6 font-monospace ${
                        errors.cost_price ? "is-invalid" : ""
                      }`}
                      placeholder="0.00"
                      value={formData.cost_price}
                      onChange={handleInputChange}
                    />
                    {errors.cost_price && (
                      <div className="invalid-feedback">{errors.cost_price}</div>
                    )}
                  </div>
                  <small className="text-muted d-block mt-1">Acquisition cost per unit</small>
                  {!hasChoices && Number(formData.cost_price || 0) > 0 &&
                    Number(formData.selling_price || 0) > 0 &&
                    Number(formData.selling_price || 0) <= Number(formData.cost_price || 0) && (
                      <small className="text-danger fw-semibold d-block mt-1">
                        ⚠️ Selling Price (Rs. {Number(formData.selling_price)}) must be greater than Unit Cost (Rs. {Number(formData.cost_price)}).
                      </small>
                    )}
                </div>
                )}

                {!hasChoices && (
                <div className="col-12 col-md-4">
                  <label className="form-label fw-semibold text-dark small mb-1">
                    Current Stock on Hand <span className="text-danger">*</span>
                  </label>
                  <div className="input-group">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() =>
                        setFormData((prev) => ({
                          ...prev,
                          qty: Math.max(0, Number(prev.qty || 0) - 1),
                        }))
                      }
                    >
                      <FaMinus size={10} />
                    </button>
                    <input
                      type="number"
                      min="0"
                      name="qty"
                      className={`form-control text-center fw-bold fs-6 font-monospace ${
                        errors.qty ? "is-invalid" : ""
                      }`}
                      value={formData.qty}
                      onChange={handleInputChange}
                    />
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() =>
                        setFormData((prev) => ({
                          ...prev,
                          qty: Number(prev.qty || 0) + 1,
                        }))
                      }
                    >
                      <FaPlus size={10} />
                    </button>
                    <span className="input-group-text text-muted">units</span>
                  </div>
                  <small className="text-muted d-block mt-1">Physical stock quantity</small>
                </div>
                )}

                {!hasChoices && (
                <div className="col-12">
                  <div className="p-3 bg-light rounded-3 border">
                    <div className="row g-2 text-center text-md-start align-items-center">
                      <div className="col-6 col-md-3 border-end">
                        <span className="text-muted small d-block" style={{ fontSize: "11.5px" }}>Profit Per Unit</span>
                        <span className={`fw-bold font-monospace fs-6 ${grossProfitPerUnit >= 0 ? "text-success" : "text-danger"}`}>
                          Rs. {grossProfitPerUnit.toLocaleString()}
                        </span>
                      </div>
                      <div className="col-6 col-md-3 border-end">
                        <span className="text-muted small d-block" style={{ fontSize: "11.5px" }}>Gross Margin</span>
                        <span className={`badge rounded-pill ${marginPercent >= 25 ? "bg-success" : marginPercent > 0 ? "bg-primary" : "bg-danger"}`}>
                          {marginPercent}% margin
                        </span>
                      </div>
                      <div className="col-6 col-md-3 border-end">
                        <span className="text-muted small d-block" style={{ fontSize: "11.5px" }}>Inventory Valuation (Retail)</span>
                        <span className="fw-bold text-dark font-monospace">
                          Rs. {totalValuation.toLocaleString()}
                        </span>
                      </div>
                      <div className="col-6 col-md-3">
                        <span className="text-muted small d-block" style={{ fontSize: "11.5px" }}>Total Stock Cost</span>
                        <span className="fw-bold text-secondary font-monospace">
                          Rs. {totalStockCost.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                )}
              </div>
            </div>

            {/* Card 3: Shopify-Style Search Engine Optimization (SEO) */}
            <ShopifySEOEditor
              itemName={formData.name}
              itemBrand={formData.brand}
              itemCategoryName={selectedCategoryObj?.name}
              itemDescription={formData.discription}
              itemSlug={formData.slug}
              metaTitle={formData.meta_title}
              metaDescription={formData.meta_descrip}
              metaKeywords={formData.meta_keyword}
              type="product"
              onChange={(updated) => {
                setFormData((prev) => ({
                  ...prev,
                  ...updated,
                }));
              }}
            />
          </div>

          {/* RIGHT COLUMN: Media & Store Status */}
          <div className="col-12 col-lg-4">
            {/* Card 4: Store Visibility & Badges */}
            <div className="admin-card bg-white rounded-3 shadow-sm border-0 mb-4 p-4">
              <h5 className="fw-bold text-dark mb-3 border-bottom pb-2">
                Visibility & Promotion
              </h5>

              <div className="d-flex flex-column gap-3">
                <div className="form-check form-switch p-0 d-flex align-items-center justify-content-between">
                  <div>
                    <label className="form-check-label fw-bold text-dark d-block" htmlFor="statusSwitch">
                      <FaEye className="text-primary me-1" /> Show on the store
                    </label>
                    <small className="text-muted">{formData.on_store ? "Customers can find and buy this product." : "Saved in your catalog. Customers will not see it."}</small>
                  </div>
                  <input
                    className="form-check-input ms-2"
                    type="checkbox"
                    role="switch"
                    id="statusSwitch"
                    name="on_store"
                    checked={formData.on_store}
                    onChange={handleInputChange}
                    style={{ width: "2.4em", height: "1.3em", cursor: "pointer" }}
                  />
                </div>

                <div className="form-check form-switch p-0 d-flex align-items-center justify-content-between border-top pt-3">
                  <div>
                    <label className="form-check-label fw-bold text-dark d-block" htmlFor="featuredSwitch">
                      <FaStar className="text-warning me-1" /> Featured Product
                    </label>
                    <small className="text-muted">Showcase in Featured collections</small>
                  </div>
                  <input
                    className="form-check-input ms-2"
                    type="checkbox"
                    role="switch"
                    id="featuredSwitch"
                    name="featured"
                    checked={formData.featured}
                    onChange={handleInputChange}
                    style={{ width: "2.4em", height: "1.3em", cursor: "pointer" }}
                  />
                </div>

                <div className="form-check form-switch p-0 d-flex align-items-center justify-content-between border-top pt-3">
                  <div>
                    <label className="form-check-label fw-bold text-dark d-block" htmlFor="popularSwitch">
                      <FaFire className="text-danger me-1" /> Trending / Popular
                    </label>
                    <small className="text-muted">Display 'Trending' badge to shoppers</small>
                  </div>
                  <input
                    className="form-check-input ms-2"
                    type="checkbox"
                    role="switch"
                    id="popularSwitch"
                    name="popular"
                    checked={formData.popular}
                    onChange={handleInputChange}
                    style={{ width: "2.4em", height: "1.3em", cursor: "pointer" }}
                  />
                </div>
              </div>
            </div>

            {/* Card 5: Product Media Gallery */}
            <div className="admin-card bg-white rounded-3 shadow-sm border-0 mb-4 p-4">
              <h5 className="fw-bold text-dark mb-3 border-bottom pb-2 d-flex align-items-center justify-content-between">
                <span>Product Imagery</span>
                <span className="badge bg-light text-dark border">
                  {existingImages.length + newImages.length} images
                </span>
              </h5>

              {/* Existing Images */}
              {existingImages.length > 0 && (
                <div className="mb-3">
                  <label className="form-label text-muted small fw-bold mb-2">Current Gallery</label>
                  <div className="d-flex flex-wrap gap-2">
                    {existingImages.map((imgItem, idx) => {
                      const imgRaw = typeof imgItem === "string" ? imgItem : imgItem?.image;
                      if (!imgRaw || isDefaultStoreImage(imgRaw)) return null;

                      const srcUrl = imgRaw.startsWith("http") || imgRaw.startsWith("data:") || imgRaw.startsWith("blob:")
                        ? imgRaw
                        : `${imageurl}/${imgRaw.replace(/^\/+/, "")}`;

                      return (
                        <div
                          key={imgItem.id || idx}
                          className="position-relative border rounded-3 overflow-hidden bg-light"
                          style={{ width: "80px", height: "80px" }}
                        >
                          <img
                            src={srcUrl}
                            alt=""
                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                            onError={(e) => {
                              // If image fails to load, mark visually without mutating form state
                              if (e && e.target) {
                                e.target.style.opacity = "0.6";
                              }
                            }}
                          />
                          <button
                            type="button"
                            onClick={() => handleDeleteExistingImage(imgItem.id)}
                            className="btn btn-sm btn-danger position-absolute top-0 end-0 p-1 m-1 rounded-circle d-flex align-items-center justify-content-center"
                            style={{ width: "20px", height: "20px", fontSize: "10px" }}
                            title="Delete image"
                          >
                            <FaTrashAlt size={9} />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* New Images Preview */}
              {newImagePreviews.length > 0 && (
                <div className="mb-3">
                  <label className="form-label text-success small fw-bold mb-2">
                    New Images to Upload ({newImagePreviews.length})
                  </label>
                  <div className="d-flex flex-wrap gap-2">
                    {newImagePreviews.map((previewUrl, i) => (
                      <div
                        key={i}
                        className="border border-success rounded-3 overflow-hidden bg-light"
                        style={{ width: "70px", height: "70px" }}
                      >
                        <img
                          src={previewUrl}
                          alt="preview"
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Upload Input */}
              <div>
                <label className="form-label fw-semibold text-dark small mb-1">
                  Upload More Images
                </label>
                <input
                  type="file"
                  multiple
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  className="form-control form-control-sm"
                  onChange={handleFileChange}
                />
                <small className="text-muted d-block mt-1" style={{ fontSize: "11px" }}>
                  JPG, PNG, WebP or GIF photos, up to 5 MB each.
                </small>
                {errors.image && <div className="field-error alert alert-danger py-2 px-3 mt-2 mb-0 small" role="alert">{errors.image}</div>}
              </div>
            </div>

            {/* Quick Summary Save Card */}
            <div className="admin-card bg-light rounded-3 border p-3">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <span className="text-muted small">Store</span>
                <span className={`fw-bold small ${formData.on_store ? "text-success" : "text-muted"}`}>
                  {formData.on_store ? "On the store" : "Off the store"}
                </span>
              </div>
              <div className="d-flex align-items-center justify-content-between mb-3">
                <span className="text-muted small">Current Units</span>
                <span className="fw-bold text-dark font-monospace">{shownStock}</span>
              </div>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={saving}
                className="btn btn-primary w-100 fw-semibold py-2 rounded-3 shadow-sm d-flex align-items-center justify-content-center gap-2"
              >
                {saving ? "Saving Changes..." : <><FaSave /> Save Changes</>}
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
